import { supabase } from '../lib/supabase'
import { academicService } from './academicService'
import { calculateLetterGrade, isMissingAcademicSchema } from '../utils/gradeUtils'

const throwIfError = (error, fallbackMessage) => {
  if (!error) return
  const err = new Error(error.message || fallbackMessage)
  err.code = error.code
  err.cause = error
  throw err
}

export const transcriptService = {
  async listEntries(studentId) {
    const { data, error } = await supabase
      .from('transcript_entries')
      .select(`
        id,
        percent,
        letter_grade,
        teacher_name,
        source,
        notes,
        completed_at,
        subject:subjects(id, name, notes),
        term:terms(id, name, starts_on, ends_on)
      `)
      .eq('student_id', studentId)
      .order('completed_at', { ascending: false })

    if (error && isMissingAcademicSchema(error)) return []
    throwIfError(error, 'Could not load transcript')
    return data || []
  },

  async getCatalogWithProgress(studentId) {
    const [subjects, entries, classes] = await Promise.all([
      academicService.listSubjects(),
      this.listEntries(studentId),
      academicService.listClasses()
    ])

    const entryBySubjectId = new Map(entries.map((entry) => [entry.subject?.id, entry]))
    const inProgressBySubjectId = new Map()

    classes
      .filter((classItem) => classItem.status === 'active')
      .forEach((classItem) => {
        classItem.subjects.forEach((subject) => {
          if (!inProgressBySubjectId.has(subject.id)) {
            inProgressBySubjectId.set(subject.id, [])
          }
          inProgressBySubjectId.get(subject.id).push(classItem)
        })
      })

    const { data: enrollments } = await supabase
      .from('class_enrollment')
      .select('class_id')
      .eq('student_id', studentId)
      .eq('status', 'enrolled')

    const enrolledClassIds = new Set((enrollments || []).map((row) => row.class_id))

    return subjects.map((subject) => {
      const entry = entryBySubjectId.get(subject.id)
      const activeClasses = (inProgressBySubjectId.get(subject.id) || [])
        .filter((classItem) => enrolledClassIds.has(classItem.id))

      let status = 'not_taken'
      if (entry) status = 'completed'
      else if (activeClasses.length) status = 'in_progress'

      return {
        subject,
        status,
        entry: entry || null,
        currentClass: activeClasses[0] || null
      }
    })
  },

  async upsertEntry({
    studentId,
    subjectId,
    termId,
    classId,
    teacherName,
    percent,
    letterGrade,
    source,
    notes
  }) {
    const payload = {
      student_id: studentId,
      subject_id: subjectId,
      term_id: termId || null,
      class_id: classId || null,
      teacher_name: teacherName || null,
      percent: percent === null || percent === undefined || percent === '' ? null : Number(percent),
      letter_grade: letterGrade || (percent !== null && percent !== undefined && percent !== ''
        ? calculateLetterGrade(Number(percent))
        : null),
      source,
      notes: notes?.trim() || null,
      completed_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }

    const { data, error } = await supabase
      .from('transcript_entries')
      .upsert(payload, { onConflict: 'student_id,subject_id' })
      .select()
      .single()

    throwIfError(error, 'Could not save transcript entry')
    return data
  },

  async deleteEntry(entryId) {
    const { error } = await supabase
      .from('transcript_entries')
      .delete()
      .eq('id', entryId)
    throwIfError(error, 'Could not remove transcript entry')
  }
}

export default transcriptService
